import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Grid,
  Card,
  Icon,
  Dialog,
  CircularProgress,
  MenuItem,
  FormControl,
  IconButton,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import usePaymentData from "layouts/tables/data/welfareTableData";
import PropTypes from "prop-types";

function Payment() {
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayment,
    setSelectedPayment,
    handleEditSubmit,
    handlePaystackSubmit,
    handleDeleteConfirm,
    handleAddPayment,
    members,
    isLoadingMembers,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    loadAllMembers,
  } = usePaymentData();

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const editDialogRef = useRef(null);
  const deleteDialogRef = useRef(null);

  // Focus management for edit dialog
  useEffect(() => {
    if (editModalOpen && editDialogRef.current) {
      const focusableElements = editDialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [editModalOpen]);

  // Focus management for delete dialog
  useEffect(() => {
    if (deleteModalOpen && deleteDialogRef.current) {
      const focusableElements = deleteDialogRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [deleteModalOpen]);

  useEffect(() => {
    if (!selectedPayment?.reference) {
      setSelectedPayment((prev) => ({
        ...prev,
        reference: `WEL-${Date.now().toString().slice(-6)}`,
      }));
    }
  }, [selectedPayment, setSelectedPayment]);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Load members when modal opens
  useEffect(() => {
    if (editModalOpen) {
      loadAllMembers();
    }
  }, [editModalOpen, loadAllMembers]);

  const handlePaystackPayment = useCallback(async () => {
    setIsProcessingPayment(true);
    try {
      await handlePaystackSubmit();
    } finally {
      setIsProcessingPayment(false);
    }
  }, [handlePaystackSubmit]);

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading Welfare Payments...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between">
                  <Grid item>
                    <MDTypography variant="h6" color="white">
                      Welfare Payments
                    </MDTypography>
                  </Grid>
                  <Grid item>
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={handleAddPayment}
                        aria-label="Add new welfare payment"
                      >
                        <Icon>add</Icon>&nbsp;New Welfare Payment
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={handlePrint}
                        aria-label="Print payments"
                      >
                        <Icon>print</Icon>
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search welfare payment..."
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Edit/Create Payment Dialog */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="edit-payment-dialog-title"
        aria-modal="true"
        ref={editDialogRef}
      >
        <DialogTitle id="edit-payment-dialog-title">
          {selectedPayment?.id ? "Edit Payment" : "Create New Payment"}
        </DialogTitle>
        <DialogContent>
          <MDBox p={4}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <MDInput
                    label="Search Member *"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      if (e.target.value.trim() !== "") {
                        setShowMemberDropdown(true);
                      } else {
                        setShowMemberDropdown(false);
                      }
                    }}
                    onFocus={() => {
                      if (searchTerm.trim() !== "" && filteredMembers.length > 0) {
                        setShowMemberDropdown(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowMemberDropdown(false), 200)}
                    fullWidth
                    margin="normal"
                    required
                    InputProps={{
                      endAdornment: (
                        <>
                          {searchTerm && !isLoadingMembers && (
                            <IconButton
                              onClick={() => {
                                setSearchTerm("");
                                setSelectedPayment((prev) => ({
                                  ...prev,
                                  member_id: "",
                                  member_name: "",
                                }));
                              }}
                              size="small"
                              aria-label="Clear search"
                            >
                              <Icon>clear</Icon>
                            </IconButton>
                          )}
                          {isLoadingMembers && (
                            <CircularProgress color="inherit" size={20} aria-label="Loading" />
                          )}
                        </>
                      ),
                    }}
                  />
                  {showMemberDropdown && (
                    <div
                      role="listbox"
                      aria-label="Member search results"
                      style={{
                        position: "relative",
                        zIndex: 1300,
                        width: "100%",
                        maxHeight: "200px",
                        overflowY: "auto",
                        backgroundColor: "white",
                        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                        marginTop: "4px",
                      }}
                    >
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map((member) => (
                          <MenuItem
                            key={member.id}
                            onClick={() => handleMemberSelect(member)}
                            sx={{
                              padding: "8px 16px",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                            }}
                            role="option"
                            aria-selected={selectedPayment?.member_id === member.id}
                          >
                            {member.first_name} {member.surname}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled sx={{ padding: "8px 16px", color: "#999" }}>
                          {isLoadingMembers ? "Loading..." : "No members found"}
                        </MenuItem>
                      )}
                    </div>
                  )}
                </FormControl>
                {selectedPayment?.member_id && (
                  <input type="hidden" name="member_id" value={selectedPayment.member_id} />
                )}
              </Grid>

              {selectedPayment?.method === "paystack" && (
                <Grid item xs={12}>
                  <MDInput
                    label="Email *"
                    type="email"
                    value={selectedPayment?.email || ""}
                    onChange={(e) =>
                      setSelectedPayment({
                        ...selectedPayment,
                        email: e.target.value,
                      })
                    }
                    fullWidth
                    required
                    aria-required="true"
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <MDInput
                  select
                  label="Payment Type *"
                  value={selectedPayment?.type || "welfare"}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      type: e.target.value,
                    })
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "46px",
                    },
                  }}
                  aria-required="true"
                >
                  {["welfare"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDInput
                  label="Amount *"
                  type="number"
                  value={selectedPayment?.amount || ""}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      amount: e.target.value,
                    })
                  }
                  fullWidth
                  required
                  aria-required="true"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MDInput
                  select
                  label="Payment Method *"
                  value={selectedPayment?.method || "manual"}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      method: e.target.value,
                    })
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "46px",
                    },
                  }}
                  aria-required="true"
                >
                  {["manual", "paystack"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDInput
                  label="Reference Number"
                  value={selectedPayment?.reference || ""}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      reference: e.target.value,
                    })
                  }
                  fullWidth
                  InputProps={{
                    readOnly: !!selectedPayment?.reference,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MDInput
                  label="Payment Date *"
                  type="date"
                  value={selectedPayment?.paymentDate || new Date().toISOString().split("T")[0]}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      paymentDate: e.target.value,
                    })
                  }
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  aria-required="true"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <MDInput
                  select
                  label="Status"
                  value={selectedPayment?.status || "active"}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      status: e.target.value,
                    })
                  }
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "46px",
                    },
                  }}
                >
                  {["active", "inactive"].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </MenuItem>
                  ))}
                </MDInput>
              </Grid>
            </Grid>
            <MDBox display="flex" justifyContent="flex-end" mt={4}>
              <MDButton
                variant="gradient"
                color="secondary"
                onClick={() => setEditModalOpen(false)}
                mr={2}
                aria-label="Cancel payment editing"
              >
                Cancel
              </MDButton>

              {selectedPayment?.method === "manual" ? (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleEditSubmit}
                  disabled={!selectedPayment?.member_id}
                  style={{ marginLeft: 10 }}
                  aria-label={selectedPayment?.id ? "Save payment changes" : "Create new payment"}
                >
                  {selectedPayment?.id ? "Save Changes" : "Create Payment"}
                </MDButton>
              ) : (
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handlePaystackPayment}
                  disabled={
                    !selectedPayment?.member_id || !selectedPayment?.email || isProcessingPayment
                  }
                  style={{ marginLeft: 10 }}
                  aria-label="Pay via Paystack"
                >
                  {isProcessingPayment ? (
                    <>
                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                      Processing...
                    </>
                  ) : selectedPayment?.id ? (
                    "Save Changes"
                  ) : (
                    "Pay Via Paystack"
                  )}
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        aria-labelledby="delete-confirmation-dialog-title"
        aria-modal="true"
        ref={deleteDialogRef}
      >
        <DialogTitle id="delete-confirmation-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <MDBox p={3}>
            <MDTypography>Are you sure you want to delete this payment?</MDTypography>
            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton
                variant="gradient"
                color="secondary"
                onClick={() => setDeleteModalOpen(false)}
                mr={2}
                aria-label="Cancel deletion"
              >
                Cancel
              </MDButton>
              <MDButton
                variant="gradient"
                color="error"
                onClick={handleDeleteConfirm}
                aria-label="Confirm deletion"
              >
                Confirm Delete
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

Payment.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  handlePrint: PropTypes.func,
  editModalOpen: PropTypes.bool,
  setEditModalOpen: PropTypes.func,
  deleteModalOpen: PropTypes.bool,
  setDeleteModalOpen: PropTypes.func,
  selectedPayment: PropTypes.object,
  setSelectedPayment: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handlePaystackSubmit: PropTypes.func,
  handleDeleteConfirm: PropTypes.func,
  handleAddPayment: PropTypes.func,
  members: PropTypes.array,
  isLoadingMembers: PropTypes.bool,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  filteredMembers: PropTypes.array,
  showMemberDropdown: PropTypes.bool,
  setShowMemberDropdown: PropTypes.func,
  handleMemberSelect: PropTypes.func,
  loadAllMembers: PropTypes.func,
};

export default Payment;
